import { Booking } from './booking';
import { Activity } from './activity';
import { BookingPeriodEntry } from './bookingPeriodEntry';
import { Resource } from './resource';
import { LockerRoomBookingResponse } from '@app/features/locker-room-bookings/models/locker-room-booking.model';
import { StatusType } from '@app/features/booking/calender/model/models';
import { BookingPeriodRequest } from '@app/features/associations/models/bookingPeriodAssociation';
import { AvailabilityType } from '@features/booking/models/availabilityType';
import { BookingTypeName } from './enums';
import { ApiDateTime } from './dateTypes';

/**
 * A booking period as the API sends and takes it. The scalar fields can be `null` — the API sends
 * them so, and the booking dialog sends them back so (an unset field is `null`, never left out).
 */
export interface BookingPeriod {
  booking?: Booking;
  activity?: Activity | null;
  resource?: Resource | null;
  bookingPeriodEntries?: BookingPeriodEntry[];
  lockerRoomBookings?: LockerRoomBookingResponse[];
  id?: number | null;
  bookingId?: number | null;
  resourceId?: number | null;
  start?: ApiDateTime | null;
  end?: ApiDateTime | null;
  price?: number | null;
  bookingTypeId?: BookingTypeName | null;
  activityId?: number | null;
  personCount?: number | null;
  note?: string | null;
  note2?: string | null;
  note3?: string | null;
  status?: StatusType;
  statusId?: number | null;
  importId?: number;
  completedOn?: ApiDateTime | null;
  deletedOn?: ApiDateTime | null;
  createdBy?: string;
  createdOn?: ApiDateTime;
  updatedBy?: string;
  updatedOn?: ApiDateTime;
  tenantId?: number;
  userSubscriptionId?: number;
  skillLevel?: string | null;
  availabilityType?: AvailabilityType;
}

/** The fields `toBookingPeriodRequest` reads; `null` allowed where association rows send it. */
type BookingPeriodFields = {
  [K in
    | 'id'
    | 'bookingId'
    | 'resourceId'
    | 'bookingTypeId'
    | 'note'
    | 'price'
    | 'statusId'
    | 'start'
    | 'end'
    | 'activityId'
    | 'personCount'
    | 'skillLevel']?: BookingPeriod[K] | null;
};

/**
 * Converts a BookingPeriod to a BookingPeriodRequest DTO.
 * Extracts only the properties required for API update operations.
 *
 * @param bp - The BookingPeriod to convert, or any row carrying its request fields (e.g. an
 *   association's period) — only those fields are copied
 * @returns A BookingPeriodRequest with required properties for API operations
 */
export function toBookingPeriodRequest(bp: BookingPeriodFields): BookingPeriodRequest {
  return {
    id: bp.id ?? null,
    bookingId: bp.bookingId!,
    resourceId: bp.resourceId!,
    bookingTypeId: bp.bookingTypeId!,
    note: bp.note ?? null,
    price: bp.price!,
    statusId: bp.statusId!,
    start: bp.start!,
    end: bp.end!,
    activityId: bp.activityId ?? null,
    personCount: bp.personCount ?? null,
    skillLevel: bp.skillLevel ?? null,
  };
}
